import React, { useState, useEffect, useRef } from 'react'
import { Upload, Button, Modal, message } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { PlusOutlined } from '@ant-design/icons'
import { appConfig } from 'config'
import ImgCrop from 'antd-img-crop'
import { uploadAPI } from 'Store/upload/api'
import { connect, useDispatch } from 'react-redux'
import { selectUploadEvent, selectUploadFile } from 'Store/upload/selector'
import RootState from 'typings/RootState'
import styled from 'styled-components'
import 'antd/es/slider/style'
import { Config } from 'helpers/constanst'
import { MyTheme } from 'Components/common/Theme'
import { numberWithComma } from 'helpers/format'
import FilePreview from './FilePreview'

type Props = {
  currentFile: string
  onUpdateFile: (file: any) => void
  token: string
  folder: string
  acceptType?: any
  listType?: 'picture' | 'text' | 'picture-card'
  isPrivate?: boolean
  withCropImage?: boolean
  aspect?: number
  readOnly?: boolean
  maxFileSize?: number //KB
  shape?: 'rect' | 'round'
} & ReduxProps

const Uploader = (props: Props) => {
  const {
    currentFile,
    onUpdateFile,
    token,
    folder,
    listType = 'picture',
    acceptType = 'image/png,image/jpeg ,application/pdf',
    isPrivate = false,
    uploadFile,
    event,
    aspect = 1, // for crop image w/h
    withCropImage = false,
    readOnly = false,
    maxFileSize = 0,
    shape,
  } = props

  const refFile = useRef() as any
  const [file, setFile] = useState([] as any[])
  const [quality, setQuality] = useState(1)
  const dispatch = useDispatch()
  const [preview, setImagePreview] = useState({
    previewVisible: false,
    previewFile: '',
    previewTitle: '',
    previewType: '',
  })

  useEffect(() => {
    if (!currentFile) {
      setFile([])
    }
    if (currentFile) {
      const ext = currentFile.substr(currentFile.lastIndexOf('.') + 1)
      setFile([
        {
          uid: '-1',
          name: currentFile.substring(currentFile.lastIndexOf('/') + 1),
          status: 'done',
          url: appConfig.assetCdnEndpoint + currentFile,
          type: ext === 'pdf' ? 'application/pdf' : `image/${ext}`,
        },
      ])
    }
  }, [currentFile])

  useEffect(() => {
    if (!uploadFile || event.IsLoading || !refFile) return
    if (event.SubmitSuccess) {
      refFile?.current?.onSuccess(uploadFile)
    }

    if (event.SubmitError) {
      refFile?.current?.onError(event.SubmitError)
    }
  }, [event, uploadFile])

  const getBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
    })
  }

  const onPreview = async (file: any) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj)
    }

    setImagePreview({
      previewFile: file.url || file.preview,
      previewTitle: file.name,
      previewVisible: true,
      previewType: file.type,
    })
  }

  const onClosePreview = () => {
    setImagePreview({
      previewFile: '',
      previewTitle: '',
      previewVisible: false,
      previewType: '',
    })
  }

  const onChange = (data: any) => {
    const newFile = [] as any
    if (data.file.status !== 'removed') {
      newFile.push({
        ...data.file,
      })
    }
    setFile([...newFile])

    if (data.file.status === 'done') {
      onUpdateFile(data.file.response)
    }

    if (data.file.status === 'removed') {
      onUpdateFile('')
    }
  }

  const uploadImage = async (options: any) => {
    const { file } = options
    refFile.current = options
    const fmData = new FormData()
    fmData.append(`file`, file, file.name)
    // fmData.append('token', token)
    // fmData.append(`file`, file, file.name)
    // fmData.append(`field_index`, 'file')
    // fmData.append(`type`, folder)
    // fmData.append(`private`, isPrivate ? 'true' : 'false')

    dispatch(uploadAPI(fmData))
  }

  const validateMaxFileSize = (sizeInBytes: number) => {
    if (sizeInBytes > maxFileSize * 1000) {
      return false
    } else {
      return true
    }
  }

  const onBeforeCrop = (args: File) => {
    // get the file size in bytes
    const sizeInBytes: number = args.size

    // reduce file quality
    if (sizeInBytes > Config.FIVEMB_UPLOAD_FILE_SIZE_BYTES) {
      // greather than 5MB -> reduce quality 50%
      setQuality(0.5)
    } else if (sizeInBytes > Config.ONEMB_UPLOAD_FILE_SIZE_BYTES) {
      // greather than 1MB -> reduce quality 30%
      setQuality(0.7)
    } else if (sizeInBytes > Config.FIVEKB_UPLOAD_FILE_SIZE_BYTES) {
      // greather than 500KB -> reduce quality 15%
      setQuality(0.85)
    } else {
      // not reduce quality
      setQuality(1)
    }

    if (maxFileSize > 0) {
      if (!validateMaxFileSize(sizeInBytes)) {
        message.error({
          content: `ไม่สามารถอัพโหลดได้ เนื่องจากรูปภาพมีขนาดไฟล์ใหญ่เกินไป กรุณาอัพโหลดรูปภาพที่มีขนาดน้อยกว่า ${numberWithComma(
            maxFileSize,
          )}KB`,
          key: 'error',
        })
        return false
      }
    }

    return true
  }

  const uploadCard = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8, fontSize: '12px' }}>คลิกหรือลากไฟล์ เพื่ออัพโหลด</div>
    </div>
  )

  const uploadBtn = (
    <Button icon={<UploadOutlined />} disabled={readOnly}>
      คลิกเพื่ออัพโหลด
    </Button>
  )

  const UploaderForm = (
    <StyledUpload
      accept={acceptType}
      multiple={false}
      customRequest={uploadImage}
      listType={listType}
      fileList={[...file]}
      onChange={onChange}
      onPreview={onPreview}
      disabled={readOnly}
    >
      {file.length < 1 && (listType === 'picture-card' ? uploadCard : uploadBtn)}
    </StyledUpload>
  )

  const CropImage = (
    <ImgCrop
      rotate
      aspect={aspect}
      quality={quality}
      modalTitle="แก้ไขรูปภาพ"
      modalOk="ตกลง"
      modalCancel="ยกเลิก"
      beforeCrop={onBeforeCrop}
      fillColor="transparent"
      shape={shape}
    >
      {UploaderForm}
    </ImgCrop>
  )

  return (
    <>
      {withCropImage ? CropImage : UploaderForm}
      <PreviewModal
        className="image-modal"
        visible={preview.previewVisible || false}
        title={null}
        footer={null}
        onCancel={onClosePreview}
        centered
        closeIcon={' '}
      >
        <FilePreview src={preview.previewFile} title={preview.previewTitle} fileType={preview.previewType} />
      </PreviewModal>
    </>
  )
}

const PreviewModal = styled(Modal)`
  width: auto !important;
  max-width: 100% !important;
  .ant-modal-content {
    background: transparent;
    box-shadow: none;
    width: max-content;
    max-width: 100%;
  }
  .ant-modal-body {
    padding: 0;
  }
`

const StyledUpload = styled(Upload)`
  margin-top: 5px;
  .ant-upload-list-item-name {
    color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
    font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  }
`

const mapStateToProps = (state: RootState) => {
  return {
    event: selectUploadEvent(state),
    uploadFile: selectUploadFile(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(Uploader)
